import { useCallback, useState } from "react";
import { addQueryArgs } from "@wordpress/url";
import { useMultiSiteContext } from "@hooks/use-site-context";
import { useUserContext } from "@hooks/use-auth-context";
import { useRefreshTokenMutation } from "@hooks/use-refresh-token-mutation";
import { fetchWithTokenRefresh } from "@hooks/shared/fetch-with-token-refresh";
import { isValidId } from "@hooks/use-get/utils";
import type { AssetAssignment, AssetAssignmentsResponse } from "./types";

const ENDPOINT = "wp-json/smassets/v1/asset-assignments";

export const useGetAssetAssignments = () => {
  const [assignments, setAssignments] = useState<AssetAssignment[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { user } = useUserContext();
  const { getApiUrl, blogId } = useMultiSiteContext();
  const { refreshUserToken } = useRefreshTokenMutation();

  /**
   * GET /asset-assignments?blog_id=&asset_id=
   * List assignment history for a specific asset.
   */
  const getByAsset = useCallback(
    async (assetId: number): Promise<AssetAssignmentsResponse | null> => {
      if (!isValidId(assetId)) {
        setError("Invalid asset ID");
        return null;
      }

      setLoading(true);
      setError(null);

      try {
        const params: Record<string, string | number> = { asset_id: assetId };
        if (blogId !== null) params.blog_id = blogId;

        const url = addQueryArgs(getApiUrl(ENDPOINT), params);
        const response = await fetchWithTokenRefresh(
          url,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${user?.authToken ?? ""}`,
            },
          },
          () => refreshUserToken(user?.refreshToken ?? "")
        );

        if (!response.ok) {
          throw new Error(`Failed to fetch asset assignments: ${response.status}`);
        }

        const data: AssetAssignmentsResponse = await response.json();
        setAssignments(data.items);
        setTotal(data.total);
        return data;
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Error fetching asset assignments";
        setError(msg);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [getApiUrl, blogId, user?.authToken, user?.refreshToken, refreshUserToken]
  );

  /**
   * GET /asset-assignments?blog_id=&wp_user_id=
   * List assignment history for a specific user.
   */
  const getByUser = useCallback(
    async (wpUserId: number): Promise<AssetAssignmentsResponse | null> => {
      if (!isValidId(wpUserId)) {
        setError("Invalid user ID");
        return null;
      }

      setLoading(true);
      setError(null);

      try {
        const params: Record<string, string | number> = { wp_user_id: wpUserId };
        if (blogId !== null) params.blog_id = blogId;

        const url = addQueryArgs(getApiUrl(ENDPOINT), params);
        const response = await fetchWithTokenRefresh(
          url,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${user?.authToken ?? ""}`,
            },
          },
          () => refreshUserToken(user?.refreshToken ?? "")
        );

        if (!response.ok) {
          throw new Error(`Failed to fetch user assignments: ${response.status}`);
        }

        const data: AssetAssignmentsResponse = await response.json();
        setAssignments(data.items);
        setTotal(data.total);
        return data;
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Error fetching user assignments";
        setError(msg);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [getApiUrl, blogId, user?.authToken, user?.refreshToken, refreshUserToken]
  );

  return { assignments, total, loading, error, getByAsset, getByUser };
};

export type { AssetAssignment, AssetAssignmentsResponse };
